import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { INFT } from '../../../types/nft';
import { BadgeSizes, SKBadge } from '../Badge';
import { ReactComponent as Check } from '../../../assets/svg/icons/check.svg';
import { ReactComponent as AlertCircle } from '../../../assets/svg/icons/alert-circle.svg';
import { ReactComponent as OSLogo } from '../../../assets/svg/icons/os-logo.svg';
import { ReactComponent as Plus } from '../../../assets/svg/icons/plus.svg';
import { ReactComponent as Minus } from '../../../assets/svg/icons/minus.svg';
import { ReactComponent as XClose } from '../../../assets/svg/icons/x-close-bold.svg';
import { ReactComponent as Reload } from '../../../assets/svg/icons/loading.svg';

import cls from 'classnames';
import fallback from '../../../assets/fallback-image.png';
import { ButtonSizes, SKButton } from '../Button';
import { changeNFTAmount } from '../../../ducks/app/actions';
import { selectSelectedNFTs } from '../../../ducks/app/selectors';
export interface IApproveButton {
  isVisible?: boolean;
  isDisabled: boolean;
  onClick: () => void;
}

export interface ISKCard {
  nft: INFT;
  size: 'xs' | 'sm' | 'md' | 'lg';
  selectable?: boolean;
  selected?: boolean;
  onRemove?: () => void;
  onSelect?: () => void;
  approveButton?: IApproveButton;
  isApproved?: boolean;
  verified?: boolean;
  className?: string;
}

export const SKCard: React.FC<ISKCard> = ({
  nft,
  size,
  selectable,
  onRemove,
  onSelect,
  selected,
  approveButton,
  isApproved,
  verified,
  className,
}) => {
  const dispatch = useDispatch();
  const isERC1155 = nft?.type && nft?.type.toUpperCase() === 'ERC1155' ? true : false;
  const selectedNFTs = useSelector(selectSelectedNFTs);
  const nftSelectedAmount = isERC1155 ? parseInt(selectedNFTs.filter((asset) => asset.id === nft.id)[0]?.amount) : 1;
  const [nftAmount, setNftAmount] = useState(isERC1155 ? nftSelectedAmount : 1);

  useEffect(() => {
    if (!selected) {
      setNftAmount(1);
    }
  }, [selected]);
  return (
    <div
      className={cls(
        'relative z-0',
        {
          'w-7': size === 'xs',
          'w-10': size === 'sm',
          'w-12.5': size === 'md',
          'w-20.9375': size === 'lg',
        },
        className,
      )}
      onClick={selectable ? onSelect : (): void => console.log('click')}
    >
      {selected && (
        <SKBadge size={BadgeSizes.LG} color="outline" className="absolute top-2 left-2 z-30">
          Selected
        </SKBadge>
      )}
      {approveButton?.isVisible && (
        <SKButton
          color="tertiary"
          size={ButtonSizes.SM}
          iconRight={approveButton.isDisabled ? Reload : AlertCircle}
          className="absolute top-2 left-2 z-30 text-cloud-900 stroke-current"
          onClick={approveButton.onClick}
          disabled={approveButton.isDisabled}
          rightIconClasses={approveButton.isDisabled ? 'animate-spin' : ''}
        >
          Approve
        </SKButton>
      )}
      {isApproved && (
        <SKBadge size={BadgeSizes.LG} color="success" className="absolute top-2 left-2 z-30">
          Approved
        </SKBadge>
      )}
      {nft?.openseaUrl && nft?.openseaUrl !== '' && size !== 'xs' && !onRemove && (
        <a
          href={nft?.openseaUrl}
          className="opensea-link block h-2 z-30 absolute top-2 right-2"
          target="_blank"
          rel="noreferrer"
        >
          <SKButton
            className="z-20"
            color="secondary"
            iconLeft={OSLogo}
            onlyIcon={true}
            size={size === 'lg' ? ButtonSizes.LG : ButtonSizes.SM}
          />
          <p className="tooltip text-sm text-white bg-cloud-900 absolute w-max -top-6 right-0 z-10 items-center px-1.5 py-1 rounded-md">
            <span>View on OpenSea</span>
          </p>
        </a>
      )}
      {!selectable && onRemove && (
        <SKButton
          className="absolute top-1 right-1 z-20"
          color="secondary"
          iconLeft={XClose}
          onClick={onRemove}
          onlyIcon={true}
          size={ButtonSizes.SM}
        />
      )}
      <div
        className={cls('relative rounded-xl border-cloud-900 overflow-hidden', {
          'sk-cta cursor-pointer': selectable,
          border: !selected,
          'border-2': selected,
        })}
      >
        <figure
          className={cls('card__image border-b border-cloud-900', {
            'h-7': size === 'xs',
            'h-10': size === 'sm',
            'h-13.75': size === 'md',
            'h-20.9375': size === 'lg',
          })}
        >
          <img
            src={nft.imgUrl}
            alt={nft.name}
            onError={({ currentTarget }): void => {
              currentTarget.onerror = null;
              currentTarget.src = fallback;
            }}
            className="h-full w-full object-cover"
          />
        </figure>
        <div className="relative">
          {isERC1155 && selected && parseInt(nft.amount) > 1 && (
            <div className="flex justify-center items-center gap-1 py-1 w-full absolute left-0 top-0 transform -translate-y-full bg-white border-t border-b border-cloud-900">
              <SKButton
                size={ButtonSizes.SM}
                color="secondary"
                onlyIcon={true}
                iconLeft={Minus}
                className="min-h-2"
                onClick={(ev: React.MouseEvent<HTMLButtonElement>): void => {
                  ev.stopPropagation();
                  if (nftAmount > 1) {
                    setNftAmount(nftAmount - 1);
                    dispatch(changeNFTAmount({ id: nft.id, amount: (nftAmount - 1).toString() }));
                  }
                }}
              />
              <SKBadge className="min-w-4.5" size={BadgeSizes.LG} color="outline">
                {nftAmount}
              </SKBadge>
              <SKButton
                size={ButtonSizes.SM}
                className="text-cloud-900 stroke-current"
                color="secondary"
                onlyIcon={true}
                iconLeft={Plus}
                onClick={(ev: React.MouseEvent<HTMLButtonElement>): void => {
                  ev.stopPropagation();
                  if (nftAmount < parseInt(nft.amount)) {
                    setNftAmount(nftAmount + 1);
                    dispatch(changeNFTAmount({ id: nft.id, amount: (nftAmount + 1).toString() }));
                  }
                }}
              />
            </div>
          )}
          <div
            className={cls('flex items-center justify-between text-xs border-b border-cloud-900 bg-white', {
              'py-0.5': isERC1155,
              'py-1': !isERC1155,
              'px-1': size === 'xs' || size === 'sm',
              'px-2': size === 'md' || size === 'lg',
              'h-2.625': size === 'lg',
              'h-2': size === 'xs',
            })}
          >
            <span
              className={cls('font-bold text-cloud-900', {
                'text-xs': size === 'sm',
                'text-sm': size === 'lg',
              })}
            >
              {nft.type && nft.type.toUpperCase()}
            </span>
            {isERC1155 && (
              <SKBadge color={'black'} size={BadgeSizes.SM}>
                {nft.amount}
              </SKBadge>
            )}
          </div>
          <div
            className={cls('bg-white', {
              'py-0.75': size === 'xs',
              'py-1': size === 'sm',
              'py-1.5': size === 'md',
              'py-2': size === 'lg',
              'px-1': size === 'xs' || size === 'sm',
              'px-2': size === 'md' || size === 'lg',
            })}
          >
            <p
              className={cls('font-bold text-cloud-900 line-clamp-1', {
                'text-base mb-1': size === 'lg',
                'text-sm': size === 'sm' || size === 'md',
                'mb-2.25': !nft.issuer,
              })}
            >
              {nft.name ? nft.name : ''}
            </p>

            <p
              className={cls('text-cloud-400 line-clamp-1', {
                'text-base mb-1.5': size === 'lg',
                'text-sm': size === 'sm' || size === 'md',
              })}
            >
              {nft.issuer ? nft.issuer : ''}
            </p>

            {size && (size === 'md' || size === 'lg') && (
              <SKBadge
                color={verified ? 'success' : 'warning'}
                size={size === 'md' ? BadgeSizes.SM : BadgeSizes.LG}
                iconRight={verified ? Check : AlertCircle}
                className="mt-0.5"
              >
                {verified ? 'Verified' : 'Not verified'} by OpenSea
              </SKBadge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
